/**
 * A room's Audience in Season: its numbers from registered to customers, its people in groups with a send to each,
 * who registered last, and where they came from. The next live and the funnel also stand alone for the iPad's right pane.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { labelParts } from "../../../shared/live-time";
import type { HostRoomRow } from "../../../shared/talk";
import type { RoomGroup, WebinarDetail } from "../../../shared/types";
import { studioPath } from "../../board/LiveStrip";
import { invitable } from "../../components/InviteSheet";
import { Fold } from "../../components/season/Fold";
import { GroupedList, ListRow } from "../../components/season/GroupedList";
import type { SeasonLayout } from "../../components/season/layout";
import { Funnel } from "../../components/season/Numbers";
import { GroupHead } from "../../components/season/PageHead";
import { SeasonCard } from "../../components/season/Card";
import { Button } from "../../components/ui/Button";
import { Face, FaceStack, Ring } from "../../components/ui/Face";
import { Icon } from "../../components/ui/Icon";
import { Seg } from "../../components/ui/Seg";
import { AfterSecondLive } from "../../talk/parts";
import { allTimeHead, lastRun, longDay, teamTestsLine } from "./liveWords";
import { PrivateSendSheet } from "./PrivateSendSheet";
import { nextEyebrow, useCameFaces, watchLine, whenRegistered } from "./RoomRail";
import { groupLine, leftOutLine, sentLine, sourceRows } from "./roomWords";
import { circleMadeFrom, RoomTalkCard } from "./RoomTalk";
import { useAudience } from "./useAudience";

const n = (v: number) => v.toLocaleString("en-US");
const facesOf = (g: RoomGroup) => g.faces.map((f) => ({ name: f.name, photo: f.avatar_url }));
/** A phone shows this many groups before "Show N more". */
const PHONE_GROUPS = 3;

/** How people moved from registered to became customers, all time. */
export function FunnelCard({ detail }: { detail: WebinarDetail }) {
  const c = detail.chain;
  const { title } = allTimeHead(detail);
  const tests = detail.team_tests > 0 ? <p className="k-rm rm-tests">{teamTestsLine(detail.team_tests)}</p> : null;
  if (!c.registered) {
    return (
      <SeasonCard>
        <h2 className="k-card-h">{title}</h2>
        {tests}
        <p className="k-rm">No one has registered yet.</p>
      </SeasonCard>
    );
  }
  const steps = [
    { label: "Registered", value: c.registered },
    { label: "Came", value: c.came },
    { label: "Reached the offer", value: c.reached },
    { label: "Clicked the offer", value: c.clicked },
    { label: "Became customers", value: c.customers },
  ].map((s) => ({ ...s, shown: n(s.value) }));
  const notYet = c.clicked - c.customers;
  return (
    <SeasonCard>
      <h2 className="k-card-h">{title}</h2>
      {tests}
      <Funnel steps={steps} of={c.registered} />
      {notYet > 0 ? <p className="k-rm rm-fn">{n(notYet)} clicked and did not become customers.</p> : null}
    </SeasonCard>
  );
}

/** Where the people who registered came from, biggest first. */
function SourcesCard({ detail }: { detail: WebinarDetail }) {
  const rows = sourceRows(detail.reg_stats?.sources ?? []);
  if (!rows.length) return null;
  return (
    <SeasonCard>
      <h2 className="k-card-h">Where they came from</h2>
      <ul className="rm-src">
        {rows.map((r, i) => (
          <li key={r.key}>
            <span>{r.label}</span>
            <i className={i ? "b" : undefined}>
              <u style={{ width: `${r.bar}%` }} />
            </i>
            <b>{r.percent}%</b>
          </li>
        ))}
      </ul>
    </SeasonCard>
  );
}

/** The room's next live, the one that ran, or who is watching now: the top of the iPad's right pane. */
function NextCard({ detail, onInvite, onWatchReplay }: { detail: WebinarDetail; onInvite: () => void; onWatchReplay: () => void }) {
  const navigate = useNavigate();
  const live = detail.live;
  const ran = live?.series ? lastRun(live.series.sessions) : null;
  const at = ran ?? live;
  const came = ran ? ran.cameLive : (live?.cameLive ?? 0);
  const faces = useCameFaces(detail.id, ran ? ran.id : (live?.sessionId ?? ""), live && live.state === "ended" ? came : 0);
  const watcher = detail.watching[0];
  const who = watcher ? (
    <button type="button" className="rm-who" onClick={() => navigate(`/people/${watcher.registrantId}`)}>
      <Ring name={watcher.name} photo={watcher.avatar_url} size={34} percent={watcher.pct} live />
      <span>
        <b>Watching now: <bdi>{watcher.name}</bdi></b>
        <span>{live ? `The replay, ${watcher.stay.replace(" so far", "")}` : watcher.stay}</span>
      </span>
    </button>
  ) : null;
  const invite =
    invitable({ status: detail.status, live }) ? (
      <Button variant="p" icon="userplus" onClick={onInvite}>
        Invite people
      </Button>
    ) : null;

  if (live && live.state !== "ended" && !live.series?.ended) {
    const seats = live.series?.seats ?? live.registered;
    const people = detail.recent_registrants.slice(0, 5).map((r) => ({ name: r.name }));
    return (
      <SeasonCard className="rm-next">
        <p className="rm-eb">{nextEyebrow(live, Date.now())}</p>
        <h2 className="rm-h3">{longDay(live.startsAt, live.timezone)}</h2>
        <p className="k-rm">
          {labelParts(live.startsAt, live.timezone, "long")?.time}
          {live.series ? ` · session ${live.seq}` : ""}
        </p>
        <div className="rm-aud">
          {people.length ? <FaceStack people={people} size={28} more={Math.max(0, seats - people.length)} /> : null}
          <span>
            <b>{n(seats)} registered</b> so far
          </span>
        </div>
        <div className="rm-btns">
          {invite}
          <Button icon="live" onClick={() => navigate(studioPath(detail.id))}>
            {live.state === "live" ? "Back to the studio" : "Open the studio"}
          </Button>
        </div>
        {who}
      </SeasonCard>
    );
  }
  if (live && at) {
    return (
      <SeasonCard className="rm-next">
        <p className="rm-eb">Was live</p>
        <h2 className="rm-h3">{longDay(at.startsAt, at.timezone)}</h2>
        <p className="k-rm">
          {labelParts(at.startsAt, at.timezone, "long")?.time}
          {live.replay.playable ? " · the replay is ready" : ""}
        </p>
        <div className="rm-aud">
          {faces.length ? <FaceStack people={faces.map((p) => ({ name: p.name, photo: p.avatar_url }))} size={28} more={Math.max(0, came - faces.length)} /> : null}
          <span>
            <b>{n(came)} came</b>
            {ran ? "" : `, ${n(live.stayedToEnd)} stayed to the end`}
          </span>
        </div>
        <div className="rm-btns">
          {invite}
          {live.replay.playable ? (
            <Button icon="play" onClick={onWatchReplay}>
              Watch the replay
            </Button>
          ) : null}
        </div>
        {who}
      </SeasonCard>
    );
  }
  const shown = detail.watching.slice(0, 5);
  return (
    <SeasonCard className="rm-next">
      <p className="rm-eb">Watching right now</p>
      {shown.length ? (
        <div className="rm-aud">
          <FaceStack people={shown.map((p) => ({ name: p.name, photo: p.avatar_url }))} size={30} />
          <span>
            <b className="rm-good">
              {n(detail.watching.length)} {detail.watching.length === 1 ? "person" : "people"}
            </b>
            <br />
            {watchLine(detail)}
          </span>
        </div>
      ) : (
        <p className="k-rm">No one this minute</p>
      )}
      {invite ? <div className="rm-btns">{invite}</div> : null}
    </SeasonCard>
  );
}

function Recent({ detail }: { detail: WebinarDetail }) {
  const recent = detail.recent_registrants.slice(0, 3);
  if (!recent.length) return null;
  const nowSec = Math.floor(Date.now() / 1000);
  const tz = detail.live?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <>
      <GroupHead
        title="Recent registrants"
        action={
          <Link className="k-link" to={`/people?webinar=${encodeURIComponent(detail.id)}`}>
            All
          </Link>
        }
      />
      <GroupedList>
        {recent.map((r) => (
          <ListRow
            key={r.id}
            lead={<Face name={r.name} size={44} />}
            title={r.name}
            sub={r.source ?? undefined}
            value={whenRegistered(r.created_at, nowSec, tz)}
            to={`/people/${r.id}`}
          />
        ))}
      </GroupedList>
    </>
  );
}

/** The groups, each a row that opens its send. */
function Groups({ detail, hostFirst, layout, talk }: { detail: WebinarDetail; hostFirst: string; layout: SeasonLayout; talk: HostRoomRow | null }) {
  const a = useAudience(detail, hostFirst);
  const [more, setMore] = useState(false);
  const { data } = a;
  if (a.failed && !data) {
    return (
      <div className="k-card rm-state">
        <span>We couldn&apos;t load who came.</span>
        <Button onClick={a.retry}>Try again</Button>
      </div>
    );
  }
  if (!data) return <p className="k-sub" aria-busy="true">Loading</p>;
  const session = data.scope === "session" ? data.session : null;
  // Why only people: a group nobody is in is not worth a row where the host picks who to write to.
  const groups = data.groups.filter((g) => g.count > 0);
  const cut = layout === "phone" && !more && groups.length > PHONE_GROUPS;
  const list = cut ? groups.slice(0, PHONE_GROUPS) : groups;
  const head = a.chosen ? (
    <div className="rm-scope">
      <Seg
        className="k-seg"
        label="Which people"
        value={a.scope}
        onChange={a.setScope}
        options={[
          { value: "session", label: a.segLabel },
          { value: "all", label: a.wideLabel },
        ]}
      />
    </div>
  ) : (
    <GroupHead title="Groups" />
  );
  return (
    <>
      {head}
      {groups.length === 0 && data.scope === "series" ? <AfterSecondLive /> : null}
      <GroupedList className="rm-groups">
        {list.map((g) => {
          const words = a.wordsOf(g.key);
          const act = g.sent ? words.again(g.reach) : words.act(g.reach);
          const sendable = !!act && g.reach > 0;
          const opens = sendable || g.count > 0;
          const line = groupLine(g, data.groups, detail, data.scope);
          const out = !g.sent ? leftOutLine(g.unreachable) : "";
          const made = circleMadeFrom(talk?.circles, data.scope, g.key, session?.id);
          return (
            <ListRow
              key={g.key}
              lead={<span className="rm-n">{n(g.count)}</span>}
              title={words.label}
              sub={
                <>
                  {line ? <span>{line}</span> : null}
                  {g.sent ? (
                    <span className="rm-sent">
                      <Icon name="check" /> {sentLine(g.sent, Date.now())}
                    </span>
                  ) : null}
                  {sendable ? <span className="k-clamp">{act}</span> : null}
                  {out ? <span className="rm-note">{out}</span> : null}
                  {made ? (
                    <span className="rp-circ s">
                      <Icon name="target" />
                      {made.name}
                    </span>
                  ) : null}
                </>
              }
              end={layout !== "phone" && g.faces.length ? <FaceStack people={facesOf(g).slice(0, session ? 5 : 4)} size={28} /> : undefined}
              {...(opens ? { onClick: () => a.startSend(g) } : {})}
              innerProps={{ "data-group": g.key }}
            />
          );
        })}
      </GroupedList>
      {cut ? <Fold title={`Show ${groups.length - PHONE_GROUPS} more`} isOpen={false} onClick={() => setMore(true)} /> : null}
      {a.sending ? <PrivateSendSheet key={a.sending.seed.key} send={a.sending} onClose={a.closeSend} onSent={a.retry} /> : null}
    </>
  );
}

export function AudienceSection({ detail, layout, hostFirst, talk }: { detail: WebinarDetail; layout: SeasonLayout; hostFirst: string; talk: HostRoomRow | null }) {
  if (layout === "phone") {
    return (
      <>
        <FunnelCard detail={detail} />
        <Groups detail={detail} hostFirst={hostFirst} layout={layout} talk={talk} />
        <Recent detail={detail} />
      </>
    );
  }
  if (layout === "upright") {
    return (
      <div className="k-cols rm-cols">
        <div>
          <FunnelCard detail={detail} />
          <Groups detail={detail} hostFirst={hostFirst} layout={layout} talk={talk} />
        </div>
        <div>
          {talk ? <RoomTalkCard row={talk} /> : null}
          <SourcesCard detail={detail} />
          <Recent detail={detail} />
        </div>
      </div>
    );
  }
  return (
    <>
      <Groups detail={detail} hostFirst={hostFirst} layout={layout} talk={talk} />
      <Recent detail={detail} />
    </>
  );
}

/** The iPad's right pane beside Audience. */
export function AudienceAside({ detail, onInvite, onWatchReplay }: { detail: WebinarDetail; onInvite: () => void; onWatchReplay: () => void }) {
  return (
    <>
      <NextCard detail={detail} onInvite={onInvite} onWatchReplay={onWatchReplay} />
      <FunnelCard detail={detail} />
      <SourcesCard detail={detail} />
    </>
  );
}
